import { parseStoreAccent, STORE_ACCENT_SWATCHES } from '../theme/storeAccent.js';
import { resolveApiMediaUrl } from '../api/client.js';

/** @typedef {{ prompt: () => Promise<void>, userChoice?: Promise<{ outcome: 'accepted' | 'dismissed' }> }} BeforeInstallPromptEvent */
/** @typedef {'ios' | 'android' | 'desktop'} StorePwaInstallHintKind */

/** Caminho fixo do SW — o Apache envia `Service-Worker-Allowed: /` para permitir scope por loja. */
export const STORE_PWA_SW_URL = '/pwa/store-sw.js';

/** @type {BeforeInstallPromptEvent | null} */
let cachedInstallPrompt = null;

/** @type {Set<(event: BeforeInstallPromptEvent | null) => void>} */
const installPromptListeners = new Set();

if (typeof window !== 'undefined') {
  window.addEventListener('beforeinstallprompt', (ev) => {
    if (!('prompt' in ev)) return;
    ev.preventDefault();
    cachedInstallPrompt = /** @type {BeforeInstallPromptEvent} */ (ev);
    for (const listener of installPromptListeners) {
      listener(cachedInstallPrompt);
    }
  });
  window.addEventListener('appinstalled', () => {
    cachedInstallPrompt = null;
    for (const listener of installPromptListeners) {
      listener(null);
    }
  });
}

/**
 * @param {(event: BeforeInstallPromptEvent | null) => void} listener
 * @returns {() => void}
 */
export function subscribeStorePwaInstallPrompt(listener) {
  if (cachedInstallPrompt) listener(cachedInstallPrompt);
  installPromptListeners.add(listener);
  return () => {
    installPromptListeners.delete(listener);
  };
}

/** @returns {BeforeInstallPromptEvent | null} */
export function peekStorePwaInstallPrompt() {
  return cachedInstallPrompt;
}

/** @param {BeforeInstallPromptEvent | null} event */
export function setStorePwaInstallPromptCache(event) {
  cachedInstallPrompt = event;
}

/**
 * @param {string} slug
 * @returns {string}
 */
export function storePwaManifestApiPath(slug) {
  const s = String(slug || '').trim().toLowerCase();
  return `/api/store/${encodeURIComponent(s)}/manifest.webmanifest`;
}

/**
 * @param {string} slug
 * @returns {string}
 */
export function storePwaScopePath(slug) {
  const s = String(slug || '').trim();
  return `/${encodeURIComponent(s)}/`;
}

/** @returns {boolean} */
export function isStorePwaStandalone() {
  if (typeof window === 'undefined') return false;
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    /** @type {Window & { navigator: Navigator & { standalone?: boolean } }} */ (window).navigator
      .standalone === true
  );
}

/** @returns {boolean} */
export function isIosDevice() {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent || '';
  return (
    /iPad|iPhone|iPod/.test(ua) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
  );
}

/** @returns {boolean} */
export function isAndroidDevice() {
  if (typeof navigator === 'undefined') return false;
  return /Android/i.test(navigator.userAgent || '');
}

/** @returns {boolean} */
export function isMobileLike() {
  if (typeof window === 'undefined') return false;
  const coarse = window.matchMedia('(pointer: coarse)').matches;
  const narrow = window.matchMedia('(max-width: 768px)').matches;
  return isIosDevice() || isAndroidDevice() || coarse || narrow;
}

/**
 * @returns {StorePwaInstallHintKind}
 */
export function defaultStorePwaInstallHintKind() {
  if (isIosDevice()) return 'ios';
  if (isAndroidDevice()) return 'android';
  return 'desktop';
}

/**
 * @param {{ nome?: string | null, logo_url?: string | null, theme_json?: unknown }} [tenant]
 * @returns {string}
 */
export function storePwaThemeColor(tenant) {
  const accent = parseStoreAccent(tenant?.theme_json);
  return STORE_ACCENT_SWATCHES[accent] || STORE_ACCENT_SWATCHES.green;
}

/**
 * @param {{ nome?: string | null, logo_url?: string | null, theme_json?: unknown }} [tenant]
 * @returns {string}
 */
export function storePwaAppleTouchIcon(tenant) {
  const logo = tenant?.logo_url ? resolveApiMediaUrl(String(tenant.logo_url)) : '';
  return logo || '/og-default.png';
}

/**
 * @param {string} slug
 * @param {string} href
 * @param {string} attr
 */
function upsertHeadLink(slug, href, attr) {
  const selector = `link[data-ominia-store-pwa="${attr}"][data-ominia-store-slug="${slug}"]`;
  let link = document.querySelector(selector);
  if (!link) {
    link = document.createElement('link');
    link.setAttribute('data-ominia-store-pwa', attr);
    link.setAttribute('data-ominia-store-slug', slug);
    if (attr === 'manifest') link.rel = 'manifest';
    if (attr === 'apple-touch-icon') link.rel = 'apple-touch-icon';
    document.head.appendChild(link);
  }
  link.setAttribute('href', href);
}

/**
 * @param {string} slug
 * @param {string} name
 * @param {string} content
 */
function upsertHeadMeta(slug, name, content) {
  const selector = `meta[data-ominia-store-pwa="${name}"][data-ominia-store-slug="${slug}"]`;
  let meta = document.querySelector(selector);
  if (!meta) {
    meta = document.createElement('meta');
    meta.setAttribute('data-ominia-store-pwa', name);
    meta.setAttribute('data-ominia-store-slug', slug);
    document.head.appendChild(meta);
  }
  meta.setAttribute('name', name);
  meta.setAttribute('content', content);
}

/**
 * @param {string} slug
 * @param {{ nome?: string | null, logo_url?: string | null, theme_json?: unknown }} [tenant]
 */
export function applyStorePwaHeadTags(slug, tenant) {
  if (typeof document === 'undefined' || !slug) return;

  const manifestHref = storePwaManifestApiPath(slug);
  upsertHeadLink(slug, manifestHref, 'manifest');

  const themeColor = storePwaThemeColor(tenant);
  upsertHeadMeta(slug, 'theme-color', themeColor);

  const appTitle = String(tenant?.nome || slug).trim() || slug;
  upsertHeadMeta(slug, 'apple-mobile-web-app-capable', 'yes');
  upsertHeadMeta(slug, 'apple-mobile-web-app-status-bar-style', 'default');
  upsertHeadMeta(slug, 'apple-mobile-web-app-title', appTitle);

  const touchIcon = storePwaAppleTouchIcon(tenant);
  upsertHeadLink(slug, touchIcon, 'apple-touch-icon');

  const prevTitle = document.title;
  document.title = appTitle;
  return () => {
    document.title = prevTitle;
  };
}

/**
 * @param {string} slug
 * @returns {Promise<ServiceWorkerRegistration | null>}
 */
export async function registerStorePwaServiceWorker(slug) {
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator) || !slug) {
    return null;
  }
  const scope = storePwaScopePath(slug);
  try {
    return await navigator.serviceWorker.register(STORE_PWA_SW_URL, { scope });
  } catch {
    try {
      const legacyUrl = `/${encodeURIComponent(slug)}/sw.js`;
      return await navigator.serviceWorker.register(legacyUrl, { scope });
    } catch {
      return null;
    }
  }
}

/**
 * @param {BeforeInstallPromptEvent} event
 */
export function bindBeforeInstallPrompt(event) {
  event.preventDefault();
  setStorePwaInstallPromptCache(event);
  return event;
}
